// Helper: look up Hermes campaigns that would break if a sender (email_providers
// row) is disconnected.
//
// The match chain is email + workspace_id (NOT a FK):
//   email_providers.{email, workspace_id}
//     → hermes_email_accounts WHERE email_address = email AND workspace_id = workspace_id
//     → hermes_email_campaigns WHERE email_account_id = <hermes_email_accounts.id>
//                              AND status IN ('active', 'paused')
//                              AND (emails_pending IS NULL OR emails_pending > 0)
//
// "In use" means active OR paused campaigns with remaining work. We skip
// campaigns that are effectively done (emails_pending = 0 or emails_sent >=
// recipient_count) — disconnecting can't hurt them.
//
// See src/hooks/useHermesEmailAccounts.ts (useFindOrCreateHermesAccount) for the
// canonical email+workspace → hermes_email_accounts lookup pattern.

import { supabase } from "@/lib/supabase";

export interface AffectedCampaign {
  id: string;
  name: string;
  status: "active" | "paused";
  emails_pending: number | null;
  emails_sent: number | null;
  recipient_count: number;
}

export interface DisconnectImpact {
  affectedCampaigns: AffectedCampaign[];
}

/**
 * Returns the list of Hermes campaigns that would be broken if the given
 * email provider (matched by email + workspace) is disconnected.
 *
 * Returns an empty list (not an error) if no matching hermes_email_accounts
 * row exists — that just means the sender has never been used for Hermes
 * campaigns, so disconnect is safe.
 */
export async function checkSenderDisconnectImpact(params: {
  email: string;
  workspaceId: string;
}): Promise<DisconnectImpact> {
  const { email, workspaceId } = params;

  // Step 1: email + workspace → hermes_email_accounts.id
  // There can in principle be multiple hermes_email_accounts rows for the same
  // (email, workspace) pair across time, so collect all matches rather than
  // .single()-ing.
  const { data: hermesAccounts, error: accountsError } = await supabase
    .from("hermes_email_accounts")
    .select("id")
    .eq("email_address", email)
    .eq("workspace_id", workspaceId);

  if (accountsError) {
    throw accountsError;
  }

  if (!hermesAccounts || hermesAccounts.length === 0) {
    return { affectedCampaigns: [] };
  }

  const accountIds = hermesAccounts.map((a) => a.id);

  // Step 2: hermes_email_accounts.id → hermes_email_campaigns
  // Filter to active/paused only — drafts/completed/failed/etc. won't break.
  const { data: campaigns, error: campaignsError } = await supabase
    .from("hermes_email_campaigns")
    .select("id, name, status, emails_pending, emails_sent, recipient_count")
    .in("email_account_id", accountIds)
    .in("status", ["active", "paused"]);

  if (campaignsError) {
    throw campaignsError;
  }

  if (!campaigns || campaigns.length === 0) {
    return { affectedCampaigns: [] };
  }

  // Step 3: drop effectively-done campaigns. Two redundant guards because
  // emails_pending may not be backfilled on older rows.
  const affected: AffectedCampaign[] = campaigns
    .filter((c) => {
      const pending = c.emails_pending;
      const sent = c.emails_sent ?? 0;
      const total = c.recipient_count ?? 0;

      // Explicit 0 pending — effectively done.
      if (pending === 0) return false;

      // sent >= total — effectively done (in case pending is stale/null).
      if (total > 0 && sent >= total) return false;

      return true;
    })
    .map((c) => ({
      id: c.id as string,
      name: c.name as string,
      status: c.status as "active" | "paused",
      emails_pending: c.emails_pending,
      emails_sent: c.emails_sent,
      recipient_count: c.recipient_count,
    }));

  return { affectedCampaigns: affected };
}

/**
 * Bulk-pause the given campaign IDs by setting status='paused'. Mirrors the
 * useUpdateHermesCampaign mutation's direct UPDATE against the table.
 *
 * Returns nothing — throws on error so the caller can surface it.
 */
export async function bulkPauseCampaigns(campaignIds: string[]): Promise<void> {
  if (campaignIds.length === 0) return;

  const { error } = await supabase
    .from("hermes_email_campaigns")
    .update({ status: "paused" })
    .in("id", campaignIds);

  if (error) {
    throw error;
  }
}
